import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { loadAllMarkdown } from '../utils/markdownLoader.js';
import Seo from '../components/Seo.jsx';
import { resolveAuthor } from '../data/contributors.js';
import { blogPostingSchema, breadcrumbSchema } from '../utils/schema.js';
import { toPlainText } from '../utils/plainText.js';
import BLOG_IMAGES from '../data/blog-images.json'; // s139 Phase 7a (DECISIONS #68)

// ─────────────────────────────────────────────────────────────────────────
// BLOG POST
// Renders an individual post from src/blog/*.md.
// Slimmer frontmatter than articles, tighter typography (Fraunces upright
// for display, smaller hero), single-column body matching article reading.
// ─────────────────────────────────────────────────────────────────────────

// Lazy glob — each blog post becomes its own async chunk. BlogPost needs
// every post anyway (for prev/next navigation), but at 6 files at ~12 KB
// each, parallel fetch is fine and keeps each file independently cached.
// Glob points at the enriched output of inject-blog-city-links.mjs +
// inject-blog-state-links.mjs (s42). Source of truth is src/blog/*.md;
// the prebuild chain regenerates src/blog/enriched/*.md on every dev/build.
const blogModules = import.meta.glob('../blog/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

function parseFrontmatter(raw) {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/);
  if (!match) return { frontmatter: {}, body: raw };

  const yamlText = match[1];
  const body = match[2];
  const fm = {};
  let currentKey = null;

  for (const line of yamlText.split('\n')) {
    if (line.trim() === '' || line.trim().startsWith('#')) continue;

    const topMatch = line.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (topMatch && !line.startsWith(' ')) {
      currentKey = topMatch[1];
      const value = topMatch[2].trim();
      if (value === '') {
        fm[currentKey] = null;
      } else if (value.startsWith('"') && value.endsWith('"')) {
        fm[currentKey] = value.slice(1, -1);
      } else if (value === 'true' || value === 'false') {
        fm[currentKey] = value === 'true';
      } else {
        fm[currentKey] = value;
      }
      continue;
    }

    const subMatch = line.match(/^  ([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (subMatch) {
      const subKey = subMatch[1];
      const subValue = subMatch[2].trim();
      if (!fm[currentKey] || typeof fm[currentKey] !== 'object' || Array.isArray(fm[currentKey])) {
        fm[currentKey] = {};
      }
      fm[currentKey][subKey] = subValue.startsWith('"') && subValue.endsWith('"')
        ? subValue.slice(1, -1)
        : subValue;
      continue;
    }

    const arrMatch = line.match(/^  - (.*)$/);
    if (arrMatch) {
      const item = arrMatch[1].trim();
      if (!Array.isArray(fm[currentKey])) fm[currentKey] = [];
      fm[currentKey].push(item.startsWith('"') && item.endsWith('"') ? item.slice(1, -1) : item);
    }
  }
  return { frontmatter: fm, body };
}

const CATEGORY_LABELS = {
  seasonal: 'Seasonal',
  industry: 'Industry',
  'how-to': 'How-to',
  opinion: 'Opinion',
  product: 'Product',
};

export default function BlogPost() {
  const { slug } = useParams();
  const [dark, setDark] = useState(false);
  const [content, setContent] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadAllMarkdown(blogModules, parseFrontmatter).then((posts) => {
      if (cancelled) return;
      posts.sort((a, b) =>
        (b.frontmatter.publishedDate || '').localeCompare(a.frontmatter.publishedDate || '')
      );
      setAllPosts(posts);
      setContent(posts.find((p) => p.frontmatter.slug === slug) || null);
      setLoading(false);
      window.scrollTo(0, 0);
    });
    return () => { cancelled = true; };
  }, [slug]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontMono, fontSize: 11, letterSpacing: '0.22em',
        color: T.inkFaint, padding: 40,
      }}>
        ONE MOMENT
      </div>
    );
  }

  if (!content) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontDisplay, fontSize: 24, fontStyle: 'italic',
        padding: 40,
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: T.inkFaint, marginBottom: 24 }}>Post not found.</p>
          <Link to="/blog" style={{
            color: T.sienna, textDecoration: 'none',
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
            fontStyle: 'normal',
          }}>
            ← BACK TO THE BLOG
          </Link>
        </div>
      </div>
    );
  }

  const { frontmatter: fm, body } = content;
  // s80: byline display fields come from contributors.js (one source).
  const author = resolveAuthor(fm.author);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Find prev / next posts in the chronological feed
  const idx = allPosts.findIndex((p) => p.frontmatter.slug === slug);
  const prevPost = idx > 0 ? allPosts[idx - 1].frontmatter : null;
  const nextPost = idx >= 0 && idx < allPosts.length - 1 ? allPosts[idx + 1].frontmatter : null;

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <Seo
        title={fm.metaTitle || fm.title}
        description={toPlainText(fm.metaDescription || fm.excerpt)}
        path={`/blog/${fm.slug}`}
        ogType="article"
        publishedTime={fm.publishedDate}
        modifiedTime={fm.updatedDate}
        jsonLd={[
          blogPostingSchema({ ...fm, author, image: BLOG_IMAGES[fm.slug] ? `https://windowwashingguide.com${BLOG_IMAGES[fm.slug].hero}` : undefined }, author?.slug, author?.contributor),
          breadcrumbSchema([
            { name: 'Guide', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: fm.title, path: `/blog/${fm.slug}` },
          ]),
        ]}
      />
      <style>{`
        .blog-prose p {
          font-family: ${fontDisplay};
          font-size: 18px;
          line-height: 1.7;
          color: ${T.ink};
          margin: 0 0 22px;
          font-weight: 400;
        }
        .blog-prose h2 {
          font-family: ${fontDisplay};
          font-size: 28px;
          font-weight: 500;
          line-height: 1.2;
          letter-spacing: -0.015em;
          margin: 48px 0 18px;
          color: ${T.ink};
        }
        .blog-prose h3 {
          font-family: ${fontDisplay};
          font-size: 21px;
          font-weight: 500;
          line-height: 1.25;
          margin: 32px 0 14px;
          color: ${T.ink};
        }
        .blog-prose blockquote {
          border-left: 2px solid ${T.sienna};
          padding: 4px 0 4px 24px;
          margin: 28px 0;
          font-family: ${fontDisplay};
          font-style: italic;
          font-size: 20px;
          line-height: 1.45;
          color: ${T.ink};
          font-weight: 400;
        }
        .blog-prose blockquote em { color: ${T.sienna}; }
        .blog-prose strong {
          font-weight: 600;
          color: ${T.ink};
        }
        .blog-prose em {
          font-style: italic;
          color: ${T.sienna};
        }
        .blog-prose ul, .blog-prose ol {
          font-family: ${fontDisplay};
          font-size: 17px;
          line-height: 1.65;
          color: ${T.ink};
          padding-left: 28px;
          margin: 0 0 22px;
        }
        .blog-prose li { margin-bottom: 8px; }
        .blog-prose a {
          color: ${T.sienna};
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-underline-offset: 3px;
        }
        .blog-prose hr {
          border: none;
          border-top: 1px solid ${T.rule};
          margin: 48px 0;
        }
        .blog-prose code {
          font-family: ${fontMono};
          font-size: 13px;
          background: ${T.surface};
          padding: 1px 6px;
          border-radius: 2px;
        }
        .blog-prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 28px 0;
          font-family: ${fontBody};
          font-size: 14px;
        }
        .blog-prose th {
          font-family: ${fontMono};
          font-size: 10px;
          letter-spacing: 0.15em;
          color: ${T.inkFaint};
          text-align: left;
          padding: 12px 16px 12px 0;
          border-bottom: 1px solid ${T.ink};
          font-weight: 500;
        }
        .blog-prose td {
          padding: 14px 16px 14px 0;
          border-bottom: 1px solid ${T.rule};
          color: ${T.ink};
          line-height: 1.5;
        }
      `}</style>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Breadcrumb */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.15em', color: T.inkFaint,
          marginBottom: 32,
        }}>
          <Link to="/blog" style={{ color: T.inkFaint, textDecoration: 'none' }}>
            BLOG
          </Link>
          {' / '}
          <span style={{ color: T.sienna }}>
            {CATEGORY_LABELS[fm.category]?.toUpperCase() || 'POST'}
          </span>
        </div>

        {/* Category + read time */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 20,
        }}>
          <span style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
          }}>
            {CATEGORY_LABELS[fm.category]?.toUpperCase() || 'POST'}
          </span>
          <span style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.15em', color: T.inkFaint,
          }}>
            {fm.readTime} {fm.wordCount && `· ${fm.wordCount} WORDS`}
          </span>
        </div>

        {/* Title — upright Fraunces, smaller than article hero */}
        <h1 style={{
          fontFamily: fontDisplay, fontSize: 44, fontWeight: 500,
          lineHeight: 1.08, letterSpacing: '-0.025em',
          margin: '0 0 20px',
        }}>
          {fm.title}
        </h1>

        {/* Excerpt as subtitle */}
        {fm.excerpt && (
          <p style={{
            fontFamily: fontDisplay, fontSize: 19, fontWeight: 300, fontStyle: 'italic',
            lineHeight: 1.5, color: T.inkSoft,
            margin: '0 0 32px',
          }}>
            {toPlainText(fm.excerpt)}
          </p>
        )}

        {BLOG_IMAGES[fm.slug] && (
          <figure data-region="blog-hero" style={{ margin: '0 0 32px' }}>
            <img src={BLOG_IMAGES[fm.slug].hero} alt={BLOG_IMAGES[fm.slug].alt} width={1200} height={800} loading="eager" decoding="async"
              style={{ width: '100%', height: 'auto', aspectRatio: '3 / 2', objectFit: 'cover', display: 'block', border: `1px solid ${T.rule}` }} />
            <figcaption style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em', color: T.inkFaint, marginTop: 8, textAlign: 'right' }}>
              PHOTO <a href={BLOG_IMAGES[fm.slug].creditUrl} target="_blank" rel="noopener noreferrer nofollow" style={{ color: T.inkFaint, textDecoration: 'none' }}>{BLOG_IMAGES[fm.slug].credit.toUpperCase()}</a>
              {' / '}<a href={BLOG_IMAGES[fm.slug].photoUrl} target="_blank" rel="noopener noreferrer nofollow" style={{ color: T.inkFaint, textDecoration: 'none' }}>{BLOG_IMAGES[fm.slug].source.toUpperCase()}</a>
            </figcaption>
          </figure>
        )}

        {/* Author + dateline */}
        {author && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            paddingTop: 20, paddingBottom: 20,
            borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
            marginBottom: 40,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: T.surface, border: `1px solid ${T.rule}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: fontDisplay, fontSize: 20, color: T.inkSoft,
            }}>
              {author.initial || author.name?.[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: fontBody, fontWeight: 500, fontSize: 14 }}>
                {author.name}
              </div>
              <div style={{
                fontFamily: fontMono, fontSize: 10,
                letterSpacing: '0.12em', color: T.inkFaint,
              }}>
                {author.role}
              </div>
            </div>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.12em', color: T.inkFaint,
              textAlign: 'right',
            }}>
              {fm.publishedDate && formatDate(fm.publishedDate).toUpperCase()}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="blog-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ table: ({ node, ...props }) => <div data-table-scroll style={{ overflowX: 'auto', maxWidth: '100%' }}><table {...props} /></div> }}>{body}</ReactMarkdown>
        </div>

        {/* Author outro — s80: rendered from contributors.js (one source), replacing
            the per-post italic sign-off lines that carried invented credentials */}
        {author?.bio && (
          <div style={{ marginTop: 40, paddingTop: 24, borderTop: `1px solid ${T.rule}` }}>
            <p style={{ fontFamily: fontDisplay, fontSize: 15, lineHeight: 1.6, color: T.inkSoft, margin: 0, fontStyle: 'italic' }}>
              <strong style={{ fontStyle: 'normal', color: T.ink }}>{author.name}</strong> — {author.bio}
            </p>
            {author.slug && (
              <p style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: '0.14em', margin: '14px 0 0' }}>
                <Link to={`/contributors/${author.slug}`} style={{ color: T.sienna, textDecoration: 'none' }}>
                  ALL WORK BY {author.name.split(' ')[0].toUpperCase()} →
                </Link>
                <span style={{ color: T.inkFaint }}> · </span>
                <Link to="/editorial-standards" style={{ color: T.sienna, textDecoration: 'none' }}>EDITORIAL STANDARDS →</Link>
              </p>
            )}
          </div>
        )}

        {/* Tags */}
        {Array.isArray(fm.tags) && fm.tags.length > 0 && (
          <div style={{
            marginTop: 48, paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginRight: 4,
            }}>
              FILED UNDER
            </span>
            {fm.tags.map((tag) => (
              <span key={tag} style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.1em', color: T.inkSoft,
                border: `1px solid ${T.rule}`,
                padding: '4px 10px',
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Prev/Next nav */}
        {(prevPost || nextPost) && (
          <div style={{
            marginTop: 56, paddingTop: 32,
            borderTop: `1px solid ${T.rule}`,
            display: 'grid', gap: 24,
            gridTemplateColumns: prevPost && nextPost ? '1fr 1fr' : '1fr',
          }}>
            {prevPost && (
              <Link to={`/blog/${prevPost.slug}`} style={{
                textDecoration: 'none', color: T.ink,
              }}>
                <div style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 8,
                }}>
                  ← NEWER
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 18, lineHeight: 1.3 }}>
                  {prevPost.title}
                </div>
              </Link>
            )}
            {nextPost && (
              <Link to={`/blog/${nextPost.slug}`} style={{
                textDecoration: 'none', color: T.ink,
                textAlign: prevPost ? 'right' : 'left',
              }}>
                <div style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 8,
                }}>
                  OLDER →
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 18, lineHeight: 1.3 }}>
                  {nextPost.title}
                </div>
              </Link>
            )}
          </div>
        )}

        {/* Footer */}
        <footer style={{
          marginTop: 56, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`,
        }}>
          <Link to="/blog" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO THE BLOG
          </Link>
        </footer>
      </div>
    </div>
  );
}
